import ExportUsersMappedUserProperty from '@cohort/merchants/apps/brevo/sync/ExportUsersMappedUserProperty';
import type {
  BrevoContactAttribute,
  MappedUserProperty,
} from '@cohort/merchants/apps/brevo/sync/utils';
import {buildDefaultMappedUserProperty} from '@cohort/merchants/apps/brevo/sync/utils';
import Button from '@cohort/merchants/components/buttons/Button';
import FieldLabel from '@cohort/merchants/components/form/FieldLabel';
import LoadingState from '@cohort/merchants/components/LoadingState';
import {useUserProperties} from '@cohort/merchants/hooks/api/UserProperties';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {useCohortForm} from '@cohort/merchants/hooks/contexts/form';
import type {SyncConfigFormValues} from '@cohort/merchants/pages/apps/app/utils';
import {SvgAppIcon} from '@cohort/shared-frontend/common/AppIcons';
import {PlusCircle} from '@phosphor-icons/react';
import {useEffect} from 'react';
import {useFieldArray} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';

type ExportUsersMappedUserPropertiesProps = {
  brevoContactAttributes: BrevoContactAttribute[];
};
const ExportUsersMappedUserProperties: React.FC<ExportUsersMappedUserPropertiesProps> = ({
  brevoContactAttributes,
}) => {
  const merchant = useCurrentMerchant();

  const {t} = useTranslation('app-brevo', {
    keyPrefix: 'sync.exportUsersMappedUserProperties',
  });

  const {data: userProperties, isFetched: isUserPropertiesFetched} = useUserProperties(
    merchant.id,
    {orderBy: 'name'}
  );

  const {control, watch} = useCohortForm<Extract<SyncConfigFormValues, {appId: 'brevo'}>>();

  const isExportEnabled = watch('userExportEnabled');
  const userExportConfig = watch('userExportConfig');

  const {append: addUserProperty, replace: replaceUserProperties} = useFieldArray({
    control,
    name: 'userExportConfig.userProperties',
  });

  useEffect(() => {
    if (userExportConfig?.userProperties.length === 0) {
      replaceUserProperties([buildDefaultMappedUserProperty()]);
    }
  }, [userExportConfig?.userProperties, replaceUserProperties]);

  if (!isUserPropertiesFetched) {
    return <LoadingState className="bg-white" />;
  }

  const handleAddUserProperty = (): void => {
    addUserProperty(buildDefaultMappedUserProperty());
  };

  return (
    <div className="flex flex-col gap-2">
      <FieldLabel label={t('labelUserProperties')} optional />
      <div className="flex w-full">
        <div className="flex w-[344px] flex-row items-center space-x-1.5">
          <SvgAppIcon name="cohort" width={24} height={24} />
          <p className="text-sm font-normal text-slate-700">
            <Trans
              i18nKey="sync.exportUsersMappedUserProperties.labelCohortUserProperty"
              ns="app-brevo"
              components={{bold: <span className="font-medium text-slate-700" />}}
            />
          </p>
        </div>
        <div className="flex flex-row items-center space-x-1.5">
          <SvgAppIcon name="brevo" width={24} height={24} />
          <p className="text-sm font-normal text-slate-700">
            <Trans
              i18nKey="sync.exportUsersMappedUserProperties.labelBrevoAttribute"
              ns="app-brevo"
              components={{bold: <span className="font-medium text-slate-700" />}}
            />
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {(userExportConfig?.userProperties ?? []).map((userProperty, index) => {
          const selectedUserProperty =
            userProperties?.find(property => property.id === userProperty.userPropertyId) ?? null;
          const selectedAttribute =
            brevoContactAttributes.find(
              attr => attr.name === userProperty.brevoAttributeFieldName
            ) ?? null;

          const mappedUserProperty: MappedUserProperty = {
            userProperty: selectedUserProperty,
            brevoAttribute: selectedAttribute,
          };

          return (
            <ExportUsersMappedUserProperty
              key={`field-select-${index}`}
              index={index}
              brevoContactAttributes={brevoContactAttributes}
              userProperties={userProperties}
              mappedUserProperty={mappedUserProperty}
            />
          );
        })}
      </div>
      <div className="mt-2">
        <Button variant="secondary" onClick={handleAddUserProperty} disabled={!isExportEnabled}>
          <PlusCircle size={20} className="mr-2" />
          {t('addUserPropertyButton')}
        </Button>
      </div>
    </div>
  );
};

export default ExportUsersMappedUserProperties;
